/**
 * Booking Confirmation View
 * Displayed after successful booking
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Calendar as CalendarIcon, User, Video, CheckCircle2, ClipboardList } from 'lucide-react';
import { CalendarButtons } from '@/components/booking';
import type { BookingConfirmation } from '@/services/bookingService';

interface BookingConfirmationViewProps {
  confirmation: BookingConfirmation;
  clinicName?: string;
  /**
   * Optional pre-visit intake link returned by confirmBookingV2 when the
   * appointment type has a default intake (or one was explicitly attached).
   * If present, the patient sees a "Complete pre-visit intake" CTA.
   */
  intakeLink?: string | null;
}

export const BookingConfirmationView: React.FC<BookingConfirmationViewProps> = ({
  confirmation,
  clinicName,
  intakeLink,
}) => {
  return (
    <div className='flex min-h-screen items-center justify-center bg-white p-3 sm:p-4'>
      <Card className='w-full max-w-md border-0 shadow-xl'>
        <CardContent className='p-5 sm:pb-8 sm:pt-10 text-center'>
          <div className='mx-auto mb-5 flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-green-100'>
            <CheckCircle2 className='h-8 w-8 sm:h-10 sm:w-10 text-green-600' />
          </div>

          <h1 className='mb-2 text-xl sm:text-2xl font-bold text-slate-900'>You're Booked!</h1>
          <p className='mb-6 sm:mb-8 text-sm sm:text-base text-slate-600'>{confirmation.message}</p>

          <div className='mb-6 sm:mb-8 space-y-3 sm:space-y-4 rounded-xl sm:rounded-2xl bg-slate-50 p-4 sm:p-6 text-left'>
            <div className='flex items-center gap-4'>
              <div className='flex h-10 w-10 items-center justify-center rounded-full bg-teal-100'>
                <CalendarIcon className='h-5 w-5 text-teal-600' />
              </div>
              <div>
                <p className='font-medium text-slate-900'>
                  {new Date(confirmation.appointment.start_time).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </p>
                <p className='text-slate-500'>
                  {new Date(confirmation.appointment.start_time).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </p>
              </div>
            </div>

            <Separator />

            <div className='flex items-center gap-4'>
              <div className='flex h-10 w-10 items-center justify-center rounded-full bg-slate-100'>
                <User className='h-5 w-5 text-slate-600' />
              </div>
              <div>
                <p className='font-medium text-slate-900'>{confirmation.practitioner.name}</p>
                {confirmation.practitioner.credentials && (
                  <p className='text-slate-500'>{confirmation.practitioner.credentials}</p>
                )}
              </div>
            </div>

            {confirmation.appointment.is_telehealth && (
              <>
                <Separator />
                <div className='flex items-center gap-4'>
                  <div className='flex h-10 w-10 items-center justify-center rounded-full bg-blue-100'>
                    <Video className='h-5 w-5 text-blue-600' />
                  </div>
                  <div>
                    <p className='font-medium text-slate-900'>Video Visit</p>
                    <p className='text-sm text-slate-500'>Link will be sent via email</p>
                  </div>
                </div>
              </>
            )}
          </div>

          <p className='mb-6 text-sm text-slate-500'>
            A confirmation email has been sent to your email address.
          </p>

          <CalendarButtons
            appointment={{
              start_time: confirmation.appointment.start_time,
              end_time: confirmation.appointment.end_time,
              title: `Appointment with ${confirmation.practitioner.name}`,
            }}
            practitionerName={confirmation.practitioner.name}
            clinicName={clinicName}
            telehealthLink={confirmation.appointment.telehealth_link}
          />

          {confirmation.appointment.telehealth_link && (
            <Button
              className='mt-4 w-full bg-teal-600 hover:bg-teal-700'
              onClick={() => window.open(confirmation.appointment.telehealth_link, '_blank')}
            >
              <Video className='mr-2 h-4 w-4' />
              Join Video Call
            </Button>
          )}

          {intakeLink && (
            <div className='mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left'>
              <div className='flex items-start gap-3'>
                <div className='flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 flex-shrink-0'>
                  <ClipboardList className='h-4 w-4 text-amber-700' />
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='font-medium text-slate-900 text-sm'>
                    Complete your pre-visit intake
                  </p>
                  <p className='text-xs text-slate-600 mt-0.5'>
                    Help your provider prepare. We've also emailed you the link.
                  </p>
                  <Button
                    size='sm'
                    className='mt-3 w-full sm:w-auto bg-amber-600 hover:bg-amber-700'
                    onClick={() => window.location.assign(intakeLink)}
                  >
                    Start intake now
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

